import { createSlice } from "@reduxjs/toolkit";
import {
  fetchSaasMasterModules,
  createSaasMasterModule,
  updateSaasMasterModule,
} from "../Thunks/saasMasterModulesThunk";
import { isSupersededRequestPayload } from "../helpers/asyncResource";

const initialState = {
  /** Raw API body (supports `{ success, data: [] }`); use `getListFromApiResponse` in UI. */
  data: null,
  loading: false,
  error: null,
  saving: false,
  saveError: null,
};

export const saasMasterModulesSlice = createSlice({
  name: "saasMasterModules",
  initialState,
  reducers: {
    clearSaasMasterModules: () => ({ ...initialState }),
    clearSaasMasterModuleMutationFeedback: (state) => {
      state.saving = false;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSaasMasterModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSaasMasterModules.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(fetchSaasMasterModules.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Failed to fetch SaaS modules";
      })
      .addCase(createSaasMasterModule.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createSaasMasterModule.fulfilled, (state) => {
        state.saving = false;
        state.saveError = null;
      })
      .addCase(createSaasMasterModule.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.saving = false;
        state.saveError = action.payload ?? action.error?.message ?? "Failed to create SaaS module";
      })
      .addCase(updateSaasMasterModule.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateSaasMasterModule.fulfilled, (state) => {
        state.saving = false;
        state.saveError = null;
      })
      .addCase(updateSaasMasterModule.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.saving = false;
        state.saveError = action.payload ?? action.error?.message ?? "Failed to update SaaS module";
      });
  },
});

export const { clearSaasMasterModules, clearSaasMasterModuleMutationFeedback } =
  saasMasterModulesSlice.actions;

export const selectSaasMasterModulesData = (state) => state.saasMasterModules?.data ?? null;
export const selectSaasMasterModulesLoading = (state) => !!state.saasMasterModules?.loading;
export const selectSaasMasterModulesError = (state) => state.saasMasterModules?.error ?? null;
export const selectSaasMasterModulesSaving = (state) => !!state.saasMasterModules?.saving;
export const selectSaasMasterModulesSaveError = (state) =>
  state.saasMasterModules?.saveError ?? null;

/** Shape compatible with previous `selectSaasModulesState` usage: `{ raw, loading, error }`. */
export const selectSaasMasterModulesListState = (state) => ({
  raw: state.saasMasterModules?.data ?? null,
  loading: !!state.saasMasterModules?.loading,
  error: state.saasMasterModules?.error ?? null,
});

export default saasMasterModulesSlice.reducer;
